import { useEffect, useRef, useState } from "react";

import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  IconButton,
  InputAdornment,
  Popover
} from "@mui/material";
import TextField from "@mui/material/TextField";
import { alpha } from "@mui/material/styles";
import { ArrowDropDown, ArrowDropUp, Clear, DoneAll } from "@mui/icons-material";
import { withTranslation } from "react-i18next";
import {isEmpty} from "lodash"
import TokenSelectGroup from "@plannel/components/ui/TokenSelectGroup";
import { FS } from "@plannel/utils/font-util";

const AX = "AX";
const AY = "AY";
const AZ = "AZ";
const BX = "BX";
const BY = "BY";
const BZ = "BZ";
const CX = "CX";
const CY = "CY";
const CZ = "CZ";
const DEFAULT_CLASS_CODE = [];
const ABC_XYZ_LIST = [AX, AY, AZ, BX, BY, BZ, CX, CY, CZ];
const AbcXyzFilterModal = ({ t, invClassCdList, setInvClassCdList, disabled = false, isLayout = false }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(disabled ? null : anchorEl);
  const [isFocused, setIsFocused] = useState(false);
  const [selectedAbcXyz, setSelectedAbcXyz] = useState([]);
  const [state, setState] = useState({
    AX: false, AY: false, AZ: false, BX: false, BY: false, BZ: false, CX: false, CY: false, CZ: false
  });
  const inputRef = useRef();

  const handleClick = (event) => {
    inputRef.current.focus()
    setAnchorEl(event.currentTarget);
  };

  const handleMouseEnter = () => {
    setIsFocused(true);
  };

  const handleMouseLeave = () => {
    setIsFocused(false);
  };

  const handleClose = () => {
    setInvClassCdList(selectedAbcXyz);
    setAnchorEl(null);
  };

  const onClearHandler = () => {
    setInvClassCdList(DEFAULT_CLASS_CODE);
    setSelectedAbcXyz(DEFAULT_CLASS_CODE);
  }

  const onCheckBoxHandler = (event) => {
    const name = event.target.value;
    const abcXyz = String(name);
    const checkedValue = event.target.checked;

    if (checkedValue) {
      const addAndSortClassCode = [...selectedAbcXyz, abcXyz];
      // sort 알파벳순서
      addAndSortClassCode.sort((a, b) => a.localeCompare(b));

      setSelectedAbcXyz(addAndSortClassCode);
    } else {
      const abcXyzFilter = selectedAbcXyz.filter(value => value !== abcXyz);

      setSelectedAbcXyz(abcXyzFilter);
    }
  }

  const isCheckValue = (value) => {
    return selectedAbcXyz.includes(value);
  }

  useEffect(() => {
    if (!isLayout) {
      setSelectedAbcXyz(isEmpty(invClassCdList) ? DEFAULT_CLASS_CODE : invClassCdList)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invClassCdList]);

  useEffect(() => {
    if (isLayout) {
      const obj = {}
      for (const code of ABC_XYZ_LIST) {
        if (invClassCdList.includes(code)) {
          obj[code] = true;
        } else {
          obj[code] = false;
        }
      }
      setState(obj)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /*
   * 레이아웃(User Customization) 서브모달용 — 등급 9개를 **토큰**으로 편다(2026-08-26).
   * 예전에는 체크박스 9줄을 세로로 세우면서 `height: 600` 을 박아 둬서, 360px 모달 안에서
   * 스크롤바가 생기고 아래쪽이 텅 비어 보였다. 토큰이면 두 줄에 다 들어가 스크롤이 사라진다.
   * 라벨(구역 제목)은 주지 않는다 — 모달 제목이 이미 "ABC-XYZ Class" 다.
   *
   * ⚠ 저장 순서는 ABC_XYZ_LIST 고정 순서로 맞춘다. TokenSelectGroup 은 **클릭 순서를 보존**하므로
   *   그대로 넘기면 고른 순서대로 저장돼, 같은 조합인데 값이 달라 보인다(옛 체크박스는 항상 고정 순서였다).
   */
  const handleTokenChange = (next) => {
    const ordered = ABC_XYZ_LIST.filter(code => next.includes(code));
    setState(ABC_XYZ_LIST.reduce((acc, code) => ({ ...acc, [code]: ordered.includes(code) }), {}));
    setInvClassCdList(ordered);
  };

  return ( isLayout ?
                <TokenSelectGroup
                  options={ABC_XYZ_LIST.map(code => ({ value: code, label: code }))}
                  value={ABC_XYZ_LIST.filter(code => state[code])}
                  onChange={handleTokenChange}
                  sx={{ mb: 0 }}
                />
                :
                <Box>
                  <FormControl
                    className={"invClassCd-filter"}
                    size="small"
                    sx={{mr: 1, minWidth: 140, width: 160}}
                  >
                    <TextField
                      size="small"
                      label={t("invClassCd")}
                      type="string"
                      disabled={disabled}
                      onClick={(e) => handleClick(e)}
                      onMouseEnter={() => handleMouseEnter()}
                      onMouseLeave={() => handleMouseLeave()}
                      value={invClassCdList}
                      inputRef={inputRef}
                      autoComplete="off"
                      InputProps={{
                        endAdornment: invClassCdList?.length > 0 ? (
                          <>
                            {isFocused ?
                              <IconButton size="small" aria-label="clear text" onClick={(e) => {
                                e.stopPropagation(); // 부모 onClick 호출 방지
                                onClearHandler();
                              }}>
                                <Clear sx={{fontSize: '1.2rem'}}/>
                              </IconButton> : null}
                            <InputAdornment position="end">
                              <IconButton disabled={disabled} aria-label="search" sx={{padding: 0}}
                                          size="small">{open ? <ArrowDropUp/>
                                : <ArrowDropDown/>}</IconButton>
                            </InputAdornment>
                          </>
                        ) : <InputAdornment position="end">
                          <IconButton disabled={disabled} aria-label="search" size="small"> {open ?
                            <ArrowDropUp/>
                            : <ArrowDropDown/>}</IconButton>
                        </InputAdornment>
                      }}
                    />
                  </FormControl>
                  <Popover open={Boolean(open)}
                           anchorEl={anchorEl}
                           onClose={handleClose}
                           anchorOrigin={{vertical: "bottom", horizontal: "left"}}
                           disableAutoFocus
                           disableEnforceFocus
                           TransitionProps={{timeout: 0}}
                  >
                    <Box>
                      <Box sx={{minWidth: 140, width: 160, overflowY: "auto", overflowX: "hidden"}}>
                        <FormControl component="fieldset" sx={{width: "100%"}}>
                          {/* 행 스타일: 드롭다운 공통 룩(둥근 호버·13px 라벨)과 통일 */}
                          <FormGroup sx={{ p: "4px 6px" }}>
                            {ABC_XYZ_LIST.map((code) => (
                              <FormControlLabel
                                key={code}
                                sx={{
                                  m: 0, px: 0.5, borderRadius: 1.5,
                                  // 체크된 행은 다른 드롭다운의 선택 상태와 동일한 틴트 배경
                                  bgcolor: (theme) => isCheckValue(code) ? alpha(theme.palette.primary.main, 0.12) : "transparent",
                                  "&:hover": { bgcolor: (theme) => alpha(theme.palette.primary.main, isCheckValue(code) ? 0.16 : 0.06) },
                                  "& .MuiFormControlLabel-label": { fontSize: FS.TITLE },
                                }}
                                checked={isCheckValue(code)} value={code}
                                control={<Checkbox size="small" sx={{ p: 0.75 }} />} label={code}
                                onChange={onCheckBoxHandler}/>
                            ))}
                          </FormGroup>
                        </FormControl>
                      </Box>
                      <Box>
                        <Divider sx={{
                          backgroundColor: "primary.main",
                          borderColor: "primary.main",
                          borderWidth: 0.5,
                          opacity: 0.2
                        }}/>
                        <Box
                          sx={{
                            padding: '8px',
                            display: 'flex',
                            justifyContent: 'flex-end'
                          }}>
                          <Button
                            variant="contained"
                            startIcon={<DoneAll/>}
                            size="small"
                            color="primary"
                            onClick={handleClose}>
                            {t("confirm")}
                          </Button>
                        </Box>
                      </Box>
                    </Box>
                  </Popover>
                </Box>
  );
};

export default withTranslation()(AbcXyzFilterModal);